/* eslint-disable react/jsx-no-bind */
import { MoreHoriz, PushPin, Star } from '@sparkpost/matchbox-icons';
import { formatDateTime } from '@sparkpost/report-builder/helpers/date';
import React from 'react';
import { Subaccount, TableCollection } from 'src/components';
import { ButtonLink, PageLink } from 'src/components/links';
import {
  ActionList,
  Box,
  Button,
  Column,
  Columns,
  Inline,
  Popover,
  ScreenReaderOnly,
  Table,
  Tag,
  Tooltip
} from 'src/components/matchbox';
import { PRESET_REPORT_CONFIGS } from 'src/pages/analyticsReport/constants';
import styled from 'styled-components';

const PinToDashboardAction = styled(ActionList.Action)`
  &[disabled] {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const FilterBoxWrapper = (props) => (
  <Box borderBottom="400" padding="400">
    {props}
  </Box>
);

const Icons = ({ report, pinnedReport, favoriteReportsIds }) => {
  let icons = [];
  if (pinnedReport && pinnedReport.id === report.id) {
    icons.push(
      <Tooltip
        content="Pinned to Dashboard"
        key={`tooltip-key-${report.id}`}
        id={`tooltip-id-${report.id}`}
      >
        <ScreenReaderOnly>Pinned to dashboard</ScreenReaderOnly>
        <PushPin label="Pinned to dashboard" />
      </Tooltip>
    );
  }

  if (Boolean(favoriteReportsIds.find((id) => id === report?.id))) {
    icons.push(
      <Tooltip
        content="Favorite Report"
        key={`favorite-report-${report.id}`}
        id={`favorite-report-${report.id}`}
      >
        <ScreenReaderOnly>Favorite Report</ScreenReaderOnly>
        <Star label="Favorite Report" />
      </Tooltip>
    );
  }

  return (
    <Columns>
      {icons.map((icon) => {
        return (
          <Column>
            <Box p="200">{icon}</Box>
          </Column>
        );
      })}
    </Columns>
  );
};

const Actions = ({
  id,
  handleDelete,
  handleEdit,
  handlePin,
  handleAddtoFavorites,
  reportType,
  report,
  canEdit,
  favoriteReportsIds,
  ...rest
}) => {
  let reportIsPinned = false;
  let isReportFavorite = Boolean(favoriteReportsIds.find((id) => id === report?.id));
  if (rest.pinnedReport) {
    reportIsPinned = rest.pinnedReport.id === report.id;
  }

  return (
    <Popover
      left
      top={rest.isLast}
      id={id}
      trigger={
        <Button variant="minimal" aria-controls={id} data-id={id}>
          <Button.Icon as={MoreHoriz} />
          <ScreenReaderOnly>Open Menu</ScreenReaderOnly>
        </Button>
      }
    >
      <ActionList>
        {canEdit && <ActionList.Action content="Delete" onClick={() => handleDelete(report)} />}
        <ActionList.Action content="Schedule" to={`/signals/schedule/${report.id}`} as={PageLink} />
        <PinToDashboardAction
          content="Pin to Dashboard"
          is="button"
          onClick={() => (reportIsPinned ? '' : handlePin(report, rest.pinnedReport))}
          disabled={reportIsPinned}
          tabIndex={reportIsPinned ? '-1' : 'false'}
        />
        {canEdit && <ActionList.Action content="Edit" onClick={() => handleEdit(report)} />}
        {!isReportFavorite && (
          <ActionList.Action
            content="Add to Favorites"
            onClick={() =>
              handleAddtoFavorites({
                reportId: report?.id,
                reportName: report?.name,
                addToFavorites: true
              })
            }
          />
        )}
        {isReportFavorite && (
          <ActionList.Action
            content="Remove from Favorites"
            onClick={() =>
              handleAddtoFavorites({
                reportId: report?.id,
                reportName: report?.name,
                addToFavorites: false
              })
            }
          />
        )}
      </ActionList>
    </Popover>
  );
};

const rowComponent = (cell, index) => {
  if (!cell) {
    return <Table.Cell></Table.Cell>;
  }
  const { type } = cell;
  const { id } = cell.props;

  if (type.name === 'Actions' || type.name === 'Icons') {
    return (
      <Box
        key={`row-${id}-cell-${index}`}
        as={Table.Cell}
        textAlign={['Actions', 'Icons'].includes(type.name) ? 'right' : 'left'}
      >
        {cell}
      </Box>
    );
  }

  return <Table.Cell key={`row-${id}-cell-${index}`}>{cell}</Table.Cell>;
};

export const MyReportsTab = ({
  reports,
  currentUser,
  handleReportChangeAndClose,
  handlePin,
  handleDelete,
  handleEdit,
  handleAddtoFavorites,
  pinnedReport,
  favoriteReportsIds
}) => {
  const myReports = reports.filter(({ creator }) => creator === currentUser);

  const myReportColumnHeaders = [
    { label: 'Name', sortKey: 'name' },
    { label: 'Last Modification', sortKey: 'modified' },
    {},
    {}
  ];

  const myReportsColumns = (report) => {
    const { name, modified, isLast } = report;

    const myColumns = [
      <ButtonLink
        onClick={() => {
          handleReportChangeAndClose(report);
        }}
      >
        {name}
      </ButtonLink>,
      <div>{formatDateTime.useMomentInput(modified)}</div>,
      <Inline space="400">
        <Icons
          report={report}
          pinnedReport={pinnedReport}
          favoriteReportsIds={favoriteReportsIds}
        ></Icons>
      </Inline>,
      <Actions
        id={`popover-myreports-${report.id}`}
        handlePin={handlePin}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        handleAddtoFavorites={handleAddtoFavorites}
        report={report}
        pinnedReport={pinnedReport}
        isLast={isLast}
        favoriteReportsIds={favoriteReportsIds}
        canEdit={true}
      />
    ];

    return myColumns;
  };

  const getMyReportRowComponent = (report) => {
    return <Table.Row>{myReportsColumns(report).map(rowComponent)}</Table.Row>;
  };

  return (
    <TableCollection
      rows={myReports}
      columns={myReportColumnHeaders}
      rowComponent={getMyReportRowComponent}
      wrapperComponent={Table}
      filterBox={{
        label: '',
        show: true,
        itemToStringKeys: ['name', 'modified'],
        exampleModifiers: ['name', 'modified'],
        maxWidth: '1250',
        wrapper: FilterBoxWrapper
      }}
    />
  );
};

export const AllReportsTab = ({
  reports,
  handleReportChangeAndClose,
  handlePin,
  handleDelete,
  handleEdit,
  pinnedReport,
  favoriteReportsIds,
  handleAddtoFavorites
}) => {
  const allReportColumnHeaders = [
    { label: 'Name', sortKey: 'name' },
    { label: 'Last Modification', width: '25%', sortKey: 'modified' },
    { label: 'Created By', sortKey: 'creator' },
    {},
    {},
    {}
  ];

  const getColumnsForAllReports = () => {
    return allReportColumnHeaders;
  };

  const allReportsColumns = (report) => {
    const { name, modified, creator, subaccount_id, current_user_can_edit, isLast } = report;

    //The ternary options are for preset reports, which don't have subaccount and last modified date
    const allColumns = [
      <ButtonLink
        onClick={() => {
          handleReportChangeAndClose(report);
        }}
      >
        {name}
      </ButtonLink>,
      <div>{modified ? formatDateTime.useMomentInput(modified) : ''}</div>,
      <div>{creator}</div>,
      <>
        {subaccount_id !== undefined ? (
          <Tag>
            <Subaccount id={subaccount_id} master={subaccount_id === 0} shrinkLength={12} />
          </Tag>
        ) : null}
      </>,
      <Inline space="400">
        <Icons
          report={report}
          pinnedReport={pinnedReport}
          favoriteReportsIds={favoriteReportsIds}
        ></Icons>
      </Inline>,
      <Actions
        id={`popover-allreports-${report.id}`}
        handlePin={handlePin}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        handleAddtoFavorites={handleAddtoFavorites}
        report={report}
        pinnedReport={pinnedReport}
        isLast={isLast}
        canEdit={current_user_can_edit}
        favoriteReportsIds={favoriteReportsIds}
      />
    ];

    return allColumns;
  };

  const getAllReportRowComponent = (report) => {
    return <Table.Row>{allReportsColumns(report).map(rowComponent)}</Table.Row>;
  };

  return (
    <TableCollection
      rows={[...reports, ...PRESET_REPORT_CONFIGS]}
      columns={getColumnsForAllReports()}
      rowComponent={getAllReportRowComponent}
      wrapperComponent={Table}
      filterBox={{
        label: '',
        show: true,
        itemToStringKeys: ['name', 'modified', 'creator'],
        exampleModifiers: ['name', 'modified', 'creator'],
        maxWidth: '1250',
        wrapper: FilterBoxWrapper
      }}
    />
  );
};
